import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Button, Typography } from "components/atoms";
import { CalenderIcon, Spinner, AlternateSessionsChevronIcon } from "components/atoms/Icon";
import { useLazyGetAlternateSessionsQuery, AlternateSegment } from "services/homeApi";
import { formatToLocalDate } from "utils/functions/formatToLocalDate";
import { decodeToken } from "utils/functions/decodeToken";
import { mixpanelHelper } from "utils/functions/mixPanelHelper";
import { CalendarEvent, google, ics, outlook, yahoo } from "calendar-link";
import { getTimeZone } from "utils/functions/getTimeZone";
import { AddToExternalCalenderList } from "../TIle/AddToExternalCalenderList";

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface CalendarLinks {
  icsLink: string;
  googleLink: string;
  outLookLink: string;
  yahooLink: string;
}

interface AlternateSessionsListProps {
  segmentCode: string;
  isExpanded: boolean;
  onToggle: () => void;
  theme: "light" | "dark";
}

interface AlternateSessionRowProps {
  session: AlternateSegment;
  index: number;
  theme: "light" | "dark";
  onLaunch: () => void;
  onCalendarToggle: (links: CalendarLinks, iconElement: HTMLDivElement) => void;
}

const AlternateSessionRow = ({
  session,
  index,
  theme,
  onLaunch,
  onCalendarToggle,
}: AlternateSessionRowProps) => {
  const [isLaunchEnabled, setIsLaunchEnabled] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fifteenMinsBeforeTime = dayjs
      .utc(session.StarttimeUTC)
      .local()
      .subtract(15, "m");

    const checkTime = () => {
      if (
        dayjs().isSameOrAfter(fifteenMinsBeforeTime) &&
        dayjs().isSameOrBefore(dayjs.utc(session.EndtimeUTC).local())
      ) {
        setIsLaunchEnabled(true);
      } else {
        setIsLaunchEnabled(false);
      }
    };

    checkTime();
    const interval = setInterval(checkTime, 1000);

    return () => clearInterval(interval);
  }, [session.StarttimeUTC, session.EndtimeUTC]);

  const handleAddToExternalCalender = (
    title: string,
    startTime: string,
    endTime: string
  ) => {
    let startTimeString = `${formatToLocalDate(startTime, "YYYY-MM-DD HH:mm:ss")} ${getTimeZone(startTime)}`;
    let endTimeString = `${formatToLocalDate(endTime, "YYYY-MM-DD HH:mm:ss")} ${getTimeZone(endTime)}`;

    const event: CalendarEvent = {
      title: title,
      start: startTimeString,
      end: endTimeString,
      description: "Online Classroom",
    };

    const links: CalendarLinks = {
      googleLink: google(event),
      outLookLink: outlook(event),
      yahooLink: yahoo(event),
      icsLink: ics(event),
    };

    onCalendarToggle(links, iconRef.current!);
  };

  const formattedDate = formatToLocalDate(session.StarttimeUTC, "dddd, MMMM DD");
  const formattedStartTime = formatToLocalDate(session.StarttimeUTC, "hh:mm A");
  const formattedEndTime = formatToLocalDate(session.EndtimeUTC, "hh:mm A");
  const timezone = dayjs().tz(dayjs.tz.guess()).format("z");

  return (
    <div className="alternate-session-row">
      <div className="session-info">
        <div
          className="session-icon"
          ref={iconRef}
          onClick={() =>
            handleAddToExternalCalender(
              session.Description,
              session.Starttime,
              session.Endtime
            )
          }
          >
          <CalenderIcon color={theme === "light" ? "black" : "white"} />
        </div>
        <div className="session-datetime">
          <Typography variant="h7Med" label={formattedDate} styles="session-date" />
          <span className="session-time">
            {formattedStartTime} - {formattedEndTime} {timezone}
          </span>
        </div>
      </div>

      <div className="session-instructor">
        <Typography variant="h8Med" label={session.InstructorName} />
      </div>

      {isLaunchEnabled ? (
        <div className="session-action-red">
          <Button
            label="Launch Class"
            type="score"
            icon={false}
            onClick={onLaunch}
          />
        </div>
      ) : (
        <div className="session-action">
          <Button
            label="Launch Class"
            type="score"
            icon={false}
            isDisabled={!isLaunchEnabled}
            onClick={onLaunch}
          />
        </div>
      )
      }
    </div>
  );
};

export const AlternateSessionsList = ({
  segmentCode,
  isExpanded,
  onToggle,
  theme
}: AlternateSessionsListProps) => {

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [calendarState, setCalendarState] = useState<{
    index: number;
    links: CalendarLinks;
    top: number;
    left: number;
  } | null>(null);

  const [triggerFetch, { data, isLoading, isError, isFetching }] =
    useLazyGetAlternateSessionsQuery();

  const payload = decodeToken();
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;
  const userId = payload ? payload.userid : null;

  useEffect(() => {
    if (isExpanded && !data) {
      triggerFetch({ segmentCode });
    }
  }, [isExpanded, segmentCode, data, triggerFetch]);

  // Disable scroll on the container when a calendar dropdown is open
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || calendarState === null) return;

    const preventScroll = (e: Event) => e.preventDefault();
    container.addEventListener("wheel", preventScroll, { passive: false });
    container.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      container.removeEventListener("wheel", preventScroll);
      container.removeEventListener("touchmove", preventScroll);
    };
  }, [calendarState]);

  // Close calendar dropdown when clicking outside
  useEffect(() => {
    if (calendarState === null) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.session-icon') && !target.closest('.alternate-sessions-calendar-overlay')) {
        setCalendarState(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [calendarState]);

  // Close calendar dropdown when the panel collapses
  useEffect(() => {
    if (!isExpanded) {
      setCalendarState(null);
    }
  }, [isExpanded]);

  const handleCalendarToggle = (index: number, links: CalendarLinks, iconElement: HTMLDivElement) => {
    if (calendarState?.index === index) {
      setCalendarState(null);
      return;
    }

    const containerRect = containerRef.current!.getBoundingClientRect();
    const iconRect = iconElement.getBoundingClientRect();

    setCalendarState({
      index,
      links,
      top: iconRect.bottom - containerRect.top + 4,
      left: iconRect.left - containerRect.left,
    });
  };

  const handleLaunchClass = (session: AlternateSegment) => {
    window.open(session.ClassUrl, "_blank");
    mixpanelHelper.track("Launch Alternate Class", {
      segmentID: session.ProductOfferingSegmentId,
      productCode: productCode,
      psoCode: offeringCode,
      userID: userId,
    });
  };

  const linkText = "View Alternate Class Sessions";

  return (
    <div className="alternate-sessions-container" ref={containerRef}>
      <div
        className={classNames("alternate-sessions-header", {
          expanded: isExpanded,
        })}
        onClick={onToggle}
      >
        <span className="alternate-sessions-link">{linkText}</span>
        <AlternateSessionsChevronIcon isExpanded={isExpanded} color={theme === "light" ? "#3272be" : "#329ee2"} />
      </div>

      {isExpanded && (
        <div className="alternate-sessions-list">
          <div className="alternate-sessions-heading">
            <Typography
              variant="h8Book"
              label="Alternate Sessions Available"
              styles="alternate-sessions-title"
            />
            <Typography
              variant="h8Book"
              label="Classroom launch links will be active 15 minutes before the class start time."
              styles="alternate-sessions-subtitle"
            />
          </div>

          {(isLoading || isFetching) && (
            <div className="alternate-sessions-loading">
              <Spinner theme={theme} />
            </div>
          )}

          {isError && (
            <div className="alternate-sessions-error">
              <Typography
                variant="h8Book"
                label="Unable to load alternate sessions. Please try again."
              />
            </div>
          )}

          <div
            className="alternate-sessions-scroll-container"
            ref={scrollContainerRef}
          >
            {data?.map((session, index) => (
              <AlternateSessionRow
                key={session.ProductOfferingSegmentId}
                session={session}
                index={index}
                theme={theme}
                onLaunch={() => handleLaunchClass(session)}
                onCalendarToggle={(links, iconEl) => handleCalendarToggle(index, links, iconEl)}
              />
            ))}

            {data?.length === 0 && (
              <div className="alternate-sessions-empty">
                <Typography
                  variant="h8Book"
                  label="No alternate sessions available at this time."
                />
              </div>
            )}
          </div>
        </div>
      )}

      {calendarState && (
        <div
          className="alternate-sessions-calendar-overlay"
          style={{ top: calendarState.top, left: calendarState.left }}
        >
          <AddToExternalCalenderList
            icsLink={calendarState.links.icsLink}
            googleLink={calendarState.links.googleLink}
            yahooLink={calendarState.links.yahooLink}
            outLookLink={calendarState.links.outLookLink}
            setIsExternalCalenderListShow={() => setCalendarState(null)}
          />
        </div>
      )}
    </div>
  );
};
